import {Injectable} from '@angular/core';
import {
  AuthenticationDetails,
  CodeDeliveryDetails,
  CognitoAccessToken, CognitoIdToken, CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  ICognitoUserPoolData
} from 'amazon-cognito-identity-js';
import {environment} from '../../environments/environment';
import {HttpService} from './http.service';
import {ServerResponse, ServerStatus} from '../models/types.model';
import {catchAwsError} from '../helpers/awsErrors.helper';
import {AuthResponse, IUserData} from '../models/userData.model';

const poolData: ICognitoUserPoolData = {
  UserPoolId: environment.cognito.userPoolId,
  ClientId: environment.cognito.userPoolClientId
};
if (environment.cognito['userPoolUrl']) {
  poolData.endpoint = environment.cognito['userPoolUrl'];
}

export const userPool = new CognitoUserPool(poolData);
let loggedInUser: CognitoUser | null = null;

@Injectable({
  providedIn: 'root'
})
export class AuthService extends HttpService {
  constructor() {
    super();
  }

  successfullAuthentication(result: CognitoUserSession): Partial<IUserData> {
    const accessToken: CognitoAccessToken = result.getAccessToken();
    const idToken: CognitoIdToken = result.getIdToken();
    const refreshToken: CognitoRefreshToken = result.getRefreshToken();

    this.userService.login(idToken, accessToken, refreshToken);
    const user = {
      username: accessToken.payload['username'],
      token: idToken.getJwtToken(),
      expires: idToken.getExpiration()
    };
    return user;
  }

  async signIn(email: string, password: string): Promise<AuthResponse | ServerResponse> {
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password
    });
    loggedInUser = new CognitoUser({Username: email, Pool: userPool});

    if (environment.cognito['authenticationFlow']) {
      loggedInUser.setAuthenticationFlowType(environment.cognito['authenticationFlow']);
    }

    return new Promise<AuthResponse | ServerResponse>((resolve) => {
      loggedInUser?.authenticateUser(authenticationDetails, {
        onSuccess: (session: CognitoUserSession) => {
          const user = this.successfullAuthentication(session);
          this.userService.setUserData({username: user.username as string})
          resolve({status: ServerStatus.ok, user, message: ''});
        },

        newPasswordRequired: (userAttributes, requiredAttributes) => {
          this.userService.setNewPasswordRequiredUser(email);
          resolve({status: ServerStatus.ok, code: "newPasswordRequired", message: "newPasswordRequired"});
        },

        // MFA is required on the pool but the user has no TOTP device yet
        mfaSetup: (challengeName, challengeParameters) => {
          this.userService.setMfaUser(email);
          resolve({status: ServerStatus.ok, code: "mfaSetup", message: "mfaSetup"});
        },

        // MFA is required and the user already has a TOTP device
        totpRequired: (challengeName, challengeParameters) => {
          this.userService.setMfaUser(email);
          resolve({status: ServerStatus.ok, code: "mfaRequired", message: challengeName});
        },

        onFailure: (err) => {
          loggedInUser = null;
          resolve(catchAwsError(err));
        },
      });
    });
  }

  async setNewPassword(password: string): Promise<ServerResponse> {
    return new Promise((resolve) => {
      loggedInUser?.completeNewPasswordChallenge(password, {}, {
        onSuccess: (result) => {
          this.userService.clearNewPasswordRequiredUser();
          resolve({status: ServerStatus.ok, message: ''});
        },
        // After the forced password change, the MFA challenge chains immediately
        mfaSetup: () => {
          const email = this.userService.getNewPasswordRequiredUser() || '';
          this.userService.clearNewPasswordRequiredUser();
          this.userService.setMfaUser(email);
          resolve({status: ServerStatus.ok, code: "mfaSetup", message: "mfaSetup"});
        },
        totpRequired: (challengeName) => {
          const email = this.userService.getNewPasswordRequiredUser() || '';
          this.userService.clearNewPasswordRequiredUser();
          this.userService.setMfaUser(email);
          resolve({status: ServerStatus.ok, code: "mfaRequired", message: challengeName});
        },
        onFailure: (err) => {
          resolve(catchAwsError(err));
        }
      });
    });
  }

  // Start TOTP enrollment: returns the shared secret + an otpauth:// URI to render as a QR code
  async beginTotpSetup(): Promise<ServerResponse> {
    if (!loggedInUser) {
      return {status: ServerStatus.nok, message: 'cognito.userNotAuthenticated'};
    }
    return new Promise<ServerResponse>((resolve) => {
      loggedInUser?.associateSoftwareToken({
        associateSecretCode: (secretCode: string) => {
          const account = this.userService.getMfaUser() || '';
          const issuer = environment.productName;
          const otpauthUri = `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(account)}` +
            `?secret=${secretCode}&issuer=${encodeURIComponent(issuer)}`;
          resolve({status: ServerStatus.ok, message: '', secretCode, otpauthUri});
        },
        onFailure: (err: Error) => {
          resolve(catchAwsError(err));
        }
      });
    });
  }

  // Verify the first TOTP code: completes the MFA_SETUP challenge and signs the user in
  async verifyTotpSetup(code: string): Promise<AuthResponse | ServerResponse> {
    if (!loggedInUser) {
      return {status: ServerStatus.nok, message: 'cognito.userNotAuthenticated'};
    }
    return new Promise<AuthResponse | ServerResponse>((resolve) => {
      loggedInUser?.verifySoftwareToken(code, 'Authenticator', {
        onSuccess: (session: CognitoUserSession) => {
          const user = this.successfullAuthentication(session);
          this.userService.setUserData({username: user.username as string});
          this.userService.clearMfaUser();
          resolve({status: ServerStatus.ok, user, message: ''});
        },
        onFailure: (err: Error) => {
          resolve(catchAwsError(err));
        }
      });
    });
  }

  // Answer the TOTP challenge for a user that already enrolled a device
  async submitTotpChallenge(code: string): Promise<AuthResponse | ServerResponse> {
    if (!loggedInUser) {
      return {status: ServerStatus.nok, message: 'cognito.userNotAuthenticated'};
    }
    return new Promise<AuthResponse | ServerResponse>((resolve) => {
      loggedInUser?.sendMFACode(code, {
        onSuccess: (session: CognitoUserSession) => {
          const user = this.successfullAuthentication(session);
          this.userService.setUserData({username: user.username as string});
          this.userService.clearMfaUser();
          resolve({status: ServerStatus.ok, user, message: ''});
        },
        onFailure: (err: Error) => {
          resolve(catchAwsError(err));
        }
      }, 'SOFTWARE_TOKEN_MFA');
    });
  }

  async logout() {
    const cognitoUser = userPool.getCurrentUser();
    // don't wait for Cognito to clear our tokens
    this.userService.logout();
    // TODO: should be a global signOut when password is changed
    cognitoUser?.signOut(() => {
      console.debug("auth", "AuthService.login -> Successful logout from Cognito");
    });
    loggedInUser = null;
  }

  async globalLogout() {
    const cognitoUser = userPool.getCurrentUser();
    cognitoUser?.globalSignOut({
      onSuccess: () => {
        this.userService.logout();
      },
      onFailure: (err: Error) => {
        console.debug("auth", "AuthService.globalLogout -> Cognitor error: " + err.message);
      }
    });
    loggedInUser = null;
  }

  async resetPassword(email: string): Promise<ServerResponse> {
    return new Promise((resolve) => {
      const cUser = new CognitoUser({Username: email, Pool: userPool});
      cUser.forgotPassword({
        onSuccess: (data) => {
          resolve({status: ServerStatus.ok, message: ''});
        },
        onFailure: (err: Error) => {
          resolve(catchAwsError(err));
        },
        inputVerificationCode: (data: CodeDeliveryDetails) => {
          resolve({status: ServerStatus.ok, message: data.AttributeName});
        }
      });
    });
  }

  async confirmPassword(email: string, code: string, password: string): Promise<ServerResponse> {
    return new Promise((resolve) => {
      const cUser = new CognitoUser({Username: email, Pool: userPool});
      cUser.confirmPassword(code, password, {
        onSuccess: (data) => {
          resolve({status: ServerStatus.ok, message: ''});
        },
        onFailure: (err: Error) => {
          resolve(catchAwsError(err));
        }
      });
    });
  }

  // USER //
  // async fetch(): Promise<ITenantServerResponse> {
  //   const response = await this.get('/tenant') as ITenantServerResponse;
  //   if (response.status === ServerStatus.ok) {
  //     const user = convertToIUserData(response.body);
  //     this.userService.setUserData(user);
  //     this.languageService.setLanguage(user.tenantLanguage);
  //   }
  //   return response;
  // }
}
